import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import LockIcon from '@/public/icons/approval/lock.svg';
import ChevronUpIcon from '@/public/icons/approval/chevron-up.svg';
import CornerDownLeftIcon from '@/public/icons/approval/corner-down-left.svg';
import {
  SECU_LEVEL_ALL,
  SECU_LEVEL_APPROVAL_LINE,
  SECU_LEVEL_DEPARTMENT,
} from '@/utils/approval/securityLevel';
import {
  RestrictCheckLabel,
  RestrictCheckText,
  RestrictReferrerRow,
  SectionBox,
  SectionChevron,
  SectionContent,
  SectionTitle,
  SectionTitleLabel,
  SectionTitleRight,
  SectionTitleSummary,
  SectionToggleButton,
} from './styles';
import type { ApprovalFormValues } from './types';

// 라디오는 1/5/7 만 노출. security=9(비공개)는 별도 항목이 아니라
// 7(결재라인 공개) + restrictReferrer 체크박스 ON 으로 표현/저장한다.
// 코드 ↔ 폼 상태 변환은 utils/approval/securityLevel 에 모아둔다.
const SECU_LEVEL_OPTIONS: {
  value: string;
  label: string;
  /** 접힘 상태 우측 요약 — 제목과 한 줄에 들어가야 해서 짧게 */
  summary: string;
}[] = [
  { value: SECU_LEVEL_ALL, label: '모든 임직원 공개', summary: '전체 공개' },
  {
    value: SECU_LEVEL_DEPARTMENT,
    label: '상신자와 결재라인 소속 부서 공개',
    summary: '소속 부서 공개',
  },
  {
    value: SECU_LEVEL_APPROVAL_LINE,
    label: '결재라인 · 참조인 공개',
    summary: '결재라인 · 참조인 공개',
  },
];

const SecuLevelSection = () => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<ApprovalFormValues>();
  const [open, setOpen] = useState(false);
  const secuLevel = watch('secuLevel');
  const selectedOption = SECU_LEVEL_OPTIONS.find(
    (opt) => opt.value === secuLevel,
  );

  return (
    <SectionBox>
      <SectionTitle onClick={() => setOpen((v) => !v)}>
        <SectionTitleLabel>
          <LockIcon width={24} height={24} />
          {t('공개 레벨')}
        </SectionTitleLabel>
        <SectionTitleRight>
          {!open && selectedOption && (
            <SectionTitleSummary>
              {t(selectedOption.summary)}
            </SectionTitleSummary>
          )}
          <SectionToggleButton
            type="button"
            aria-expanded={open}
            aria-label={open ? t('공개 레벨 접기') : t('공개 레벨 펼치기')}
          >
            <SectionChevron open={open}>
              <ChevronUpIcon width={12} height={12} />
            </SectionChevron>
          </SectionToggleButton>
        </SectionTitleRight>
      </SectionTitle>
      {open && (
        <SectionContent>
          <Controller
            name="secuLevel"
            control={control}
            render={({ field }) => (
              <RadioGroup {...field}>
                {SECU_LEVEL_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<RadioV2 />}
                    label={
                      <Typography variant="body2">{t(opt.label)}</Typography>
                    }
                  />
                ))}
                {field.value === SECU_LEVEL_APPROVAL_LINE && (
                  <Controller
                    name="restrictReferrer"
                    control={control}
                    render={({ field: cbField }) => (
                      <RestrictReferrerRow>
                        <CornerDownLeftIcon
                          width={20}
                          height={20}
                          style={{ flexShrink: 0 }}
                        />
                        <RestrictCheckLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={cbField.value}
                              onChange={(e) =>
                                cbField.onChange(e.target.checked)
                              }
                            />
                          }
                          label={
                            <RestrictCheckText variant="body2">
                              {t('상신 후 참조인 추가 불가')}
                            </RestrictCheckText>
                          }
                        />
                      </RestrictReferrerRow>
                    )}
                  />
                )}
              </RadioGroup>
            )}
          />
        </SectionContent>
      )}
    </SectionBox>
  );
};

export default SecuLevelSection;
